import { useCallback, useMemo, useRef, useState } from 'react';
import { applyFilters, applySort, countFilterParts, isColumnFilterActive } from './FilterEngine';
import type {
  ColumnFilter,
  FilterChangePayload,
  FilterIconState,
  FilterModel,
  FilterableColumn,
  SortDirection,
  SortModel,
} from './types';

export interface UseColumnFiltersOptions {
  columns: FilterableColumn[];
  rows?: any[];
  mode?: 'client' | 'server';
  initialFilterModel?: FilterModel;
  initialSortModel?: SortModel;
  onChange?: (payload: FilterChangePayload) => void;
}

export interface UseColumnFiltersResult {
  filterModel: FilterModel;
  sortModel: SortModel;
  processedRows: any[];
  activeFilterCount: number;

  setColumnFilter: (columnKey: string, filter: ColumnFilter) => void;
  clearColumnFilter: (columnKey: string) => void;
  clearAllFilters: () => void;

  toggleSort: (columnKey: string, direction: SortDirection) => void;
  removeSort: (columnKey: string) => void;
  clearSort: () => void;

  getIconState: (columnKey: string) => FilterIconState;
  isSortLimitReached: boolean;
}

const EMPTY: FilterModel = {};

export function useColumnFilters(options: UseColumnFiltersOptions): UseColumnFiltersResult {
  const {
    columns,
    rows = [],
    mode = 'client',
    initialFilterModel = EMPTY,
    initialSortModel = [],
    onChange,
  } = options;

  const [filterModel, setFilterModel] = useState<FilterModel>(initialFilterModel);
  const [sortModel, setSortModel] = useState<SortModel>(initialSortModel.slice(0, 1));

  const modelsRef = useRef({ filterModel, sortModel });
  modelsRef.current = { filterModel, sortModel };

  const emit = useCallback(
    (next: Partial<{ filterModel: FilterModel; sortModel: SortModel }>, changedColumnKey: string | null) => {
      onChange?.({
        filterModel: next.filterModel ?? modelsRef.current.filterModel,
        sortModel: next.sortModel ?? modelsRef.current.sortModel,
        changedColumnKey,
      });
    },
    [onChange]
  );

  const setColumnFilter = useCallback(
    (columnKey: string, filter: ColumnFilter) => {
      setFilterModel((prev) => {
        const next = { ...prev };
        if (isColumnFilterActive(filter)) {
          next[columnKey] = {
            values: filter.values?.length ? filter.values : undefined,
            selOptions: filter.selOptions?.length ? filter.selOptions : undefined,
            condition: filter.condition ?? undefined,
          };
        } else {
          delete next[columnKey];
        }
        emit({ filterModel: next }, columnKey);
        return next;
      });
    },
    [emit]
  );

  const clearColumnFilter = useCallback(
    (columnKey: string) => {
      const current = modelsRef.current;
      const nextFilterModel = { ...current.filterModel };
      const hadFilter = Boolean(nextFilterModel[columnKey]);
      delete nextFilterModel[columnKey];

      const nextSortModel = current.sortModel.filter(
        (sort) => sort.columnKey !== columnKey
      );
      const hadSort = nextSortModel.length !== current.sortModel.length;

      if (!hadFilter && !hadSort) return;

      modelsRef.current = { filterModel: nextFilterModel, sortModel: nextSortModel };
      setFilterModel(nextFilterModel);
      setSortModel(nextSortModel);
      emit({ filterModel: nextFilterModel, sortModel: nextSortModel }, columnKey);
    },
    [emit]
  );

  const clearAllFilters = useCallback(() => {
    if (Object.keys(filterModel).length === 0 && sortModel.length === 0) return;
    emit({ filterModel: EMPTY, sortModel: [] }, null);
    setFilterModel(EMPTY);
    setSortModel([]);
  }, [emit, filterModel, sortModel]);

  const toggleSort = useCallback(
    (columnKey: string, direction: SortDirection) => {
      setSortModel((prev) => {
        const existingIndex = prev.findIndex((s) => s.columnKey === columnKey);

        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          const next =
            existing.direction === direction
              ? prev.filter((s) => s.columnKey !== columnKey) // same direction -> unsort
              : prev.map((s) => (s.columnKey === columnKey ? { ...s, direction } : s));
          emit({ sortModel: next }, columnKey);
          return next;
        }

        const next = [{ columnKey, direction }];
        emit({ sortModel: next }, columnKey);
        return next;
      });
    },
    [emit]
  );

  const removeSort = useCallback(
    (columnKey: string) => {
      setSortModel((prev) => {
        if (!prev.some((s) => s.columnKey === columnKey)) return prev;
        const next = prev.filter((s) => s.columnKey !== columnKey);
        emit({ sortModel: next }, columnKey);
        return next;
      });
    },
    [emit]
  );

  const clearSort = useCallback(() => {
    setSortModel((prev) => {
      if (prev.length === 0) return prev;
      emit({ sortModel: [] }, null);
      return [];
    });
  }, [emit]);

  const processedRows = useMemo(() => {
    if (mode === 'server') return rows;
    return applySort(applyFilters(rows, filterModel, columns), sortModel, columns);
  }, [mode, rows, filterModel, sortModel, columns]);

  const activeFilterCount = useMemo(
    () => Object.values(filterModel).filter(isColumnFilterActive).length,
    [filterModel]
  );

  const getIconState = useCallback(
    (columnKey: string): FilterIconState => {
      const sortIndex = sortModel.findIndex((s) => s.columnKey === columnKey);
      return {
        filterCount: countFilterParts(filterModel[columnKey]) + (sortIndex >= 0 ? 1 : 0),
        sortDirection: sortIndex >= 0 ? sortModel[sortIndex].direction : undefined,
        sortPriority: sortIndex >= 0 ? sortIndex + 1 : undefined,
      };
    },
    [filterModel, sortModel]
  );

  return {
    filterModel,
    sortModel,
    processedRows,
    activeFilterCount,
    setColumnFilter,
    clearColumnFilter,
    clearAllFilters,
    toggleSort,
    removeSort,
    clearSort,
    getIconState,
    isSortLimitReached: false,
  };
}